import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TeranetService } from '@services/teranet/teranet.service';
import { Business, BusinessAddress } from '@models/businesses';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	BusinessActions,
	BusinessDispatch,
	BusinessState,
} from '@state/business';
import LogRocket from 'logrocket';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { dollarsToCents } from '@core/util/money';
import { MatDialogRef } from '@angular/material/dialog';
import { BusinessAddressesState } from '@state/business/address';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { htmlEncode } from '@core/util/string';
import { SnackbarService } from '@services/ui/snackbar.service';
import { NavigationService } from '@services/nav/nav.service';
import { ICON } from '@core/util/icon';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { TestHooks } from './login.hooks';
import { AuthState } from '@state/auth';
import { EncryptFunctions } from '@functions/encryption.service';
import { TeranetCredentials } from '@models/teranet';

interface AccountNameOption {
	accountName: string;
	description: string;
}

@UntilDestroy()
@Component({
	selector: 'app-teranet-login',
	templateUrl: './login.component.html',
	standalone: false,
})
export class TeranetLoginComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(BusinessUsersState.isLoggedInUserAdmin)
	isLoggedInUserAdmin: boolean;
	@SelectSnapshot(BusinessState.getBusiness) business: Business;
	@SelectSnapshot(SelectedMatter.id) saleId;
	@Select(BusinessAddressesState.getAll) addresses$: Observable<
		BusinessAddress[]
	>;
	@Select(SelectedMatter.office) matterOffice$: Observable<BusinessAddress>;
	@SelectSnapshot(AuthState.getUserId) userId: string;

	@Input() action: 'login' | 'validate' = 'login';

	FORM_VALUES = {
		ACCOUNTNAME: 'account_name',
		USERNAME: 'username',
		PASSWORD: 'password',
	};

	public lockAccountName = false;
	public form_username;
	public form_accountName;
	public form_password = '';
	public loading = false;
	public error: string;
	public errorCode: string;
	public accountNameOptions: DropdownOption<AccountNameOption>[] = [];
	public teranetAccountUrl: string;
	public submitButtonText = 'Log In';
	public showDocketNameMsg = true;
	public lock = false;
	public teranetAdminUrl: string;
	public ICON = ICON;
	public manageAccountTooltip = 'Manage account names';

	@Input() set accountName(_accountName: string) {
		if (_accountName) {
			this.form_accountName = _accountName;
			this.lockAccountName = true;
		}
	}

	@Input() set teranetAccount(_teranetAccount: TeranetCredentials) {
		if (_teranetAccount) {
			this.form_username = _teranetAccount?.username;
		}
	}

	@Input() set teranetError(_error: {
		details?: { error: string; code: string };
	}) {
		if (_error) {
			this.error = _error?.details?.error;
			this.errorCode = _error?.details?.code;
		}
	}

	constructor(
		private teranetService: TeranetService,
		public dialogRef: MatDialogRef<TeranetLoginComponent>,
		private profileDispatch: UserProfileDispatch,
		private businessDispatch: BusinessDispatch,
		private snackbar: SnackbarService,
		private navService: NavigationService,
		private encryptFunctions: EncryptFunctions,
	) {
		this.teranetAccountUrl = UrlService.Manage_Integrations();
	}

	ngOnInit(): void {
		if (!this.isLoggedInUserAdmin) {
			this.manageAccountTooltip = 'Manage account names (admin only)';
		}
		if (this.action === 'validate') {
			this.submitButtonText = 'Validate';
			this.lockAccountName = false;
		} else if (this.action === 'login') {
			this.submitButtonText = 'Log In';
		}

		combineLatest([this.addresses$, this.matterOffice$])
			.pipe(untilDestroyed(this))
			.subscribe(([addresses, matterOffice]) => {
				const options: DropdownOption<AccountNameOption>[] = [];
				if (this.form_accountName) {
					options.push({
						name: this.form_accountName,
						value: {
							accountName: this.form_accountName,
							description: 'Default',
						},
					});
				}

				const addressAccounts = addresses.filter(
					(x) => x.teranetAccountName?.length > 0,
				);

				addressAccounts?.forEach((address) => {
					if (address?.teranetAccountName?.length > 0) {
						options.push({
							name: address?.teranetAccountName,
							value: {
								accountName: address?.teranetAccountName,
								description: address?.officeName,
							},
						});
						if (
							!this.form_accountName &&
							matterOffice &&
							matterOffice.id === address.id
						) {
							this.form_accountName = address?.teranetAccountName;
						}
					}
				});

				this.accountNameOptions = options;
			});
	}

	onAccountNameSelected(
		event: DropdownResult<DropdownOption<AccountNameOption>>,
	) {
		this.form_accountName = event.result.value.accountName;
	}

	@Dispatch()
	setTeranetBalance(balance) {
		return new BusinessActions.SetTeranetBalance(balance);
	}

	async onSubmit(form: NgForm): Promise<void> {
		this.error = null;

		if (form.invalid) {
			return;
		}

		this.loading = true;

		try {
			//validate the login
			const result = await firstValueFrom(
				this.teranetService.getBalance({
					account: this.form_accountName,
					username: this.form_username,
					password: htmlEncode(this.form_password),
					source: this.action,
				}),
			);

			const encryptedPassword = await firstValueFrom(
				this.encryptFunctions.encrypt(this.form_password),
			);

			await firstValueFrom(
				this.teranetService.upsertUserToTeranetAccount({
					account: this.form_accountName,
					username: this.form_username,
					password: encryptedPassword,
					userId: this.userId,
				}),
			);

			await firstValueFrom(
				this.teranetService.updateTeranetAccountBalance(
					this.form_accountName,
					dollarsToCents(result?.account_balance),
				),
			);

			this.profileDispatch.SetTeranetUsername(this.form_username);

			if (this.accountNameOptions?.length === 0) {
				this.businessDispatch.SetTeranetAccountName(this.form_accountName);
			}

			this.setTeranetBalance(dollarsToCents(result?.account_balance));

			// save users teranet password in session
			this.profileDispatch.SetTeranetPassword(this.form_password);
			this.profileDispatch.SetTeranetAccountName(this.form_accountName);

			if (this.action === 'validate') {
				this.snackbar.openSnackBar({
					text: 'Credentials successfully validated',
					type: 'success',
				});
			}
		} catch (err) {
			if (err?.details) {
				LogRocket.error('Teranet login error', err.details);
				this.errorCode = err.details?.code;
				this.error = err.details?.error;

				// if user needs to reset password then set teranet account name, username and
				if (err.details?.code === 'DAW21045') {
					this.profileDispatch.SetTeranetPasswordExpired(true);
					this.profileDispatch.SetTeranetUsername(this.form_username);
					this.profileDispatch.SetTeranetAccountName(this.form_accountName);
					this.profileDispatch.SetTeranetPassword(this.form_password);
				}
			} else {
				const errorMsg =
					'Incorrect account name, username or password entered.';
				this.error = errorMsg;
				LogRocket.error('Unable to complete teranet login', err);
			}
		} finally {
			this.loading = false;
		}
	}

	changePassword() {
		this.profileDispatch.SetTeranetPasswordExpired(true);
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	goToIntegrations() {
		if (this.isLoggedInUserAdmin) {
			this.dialogRef.close();
			this.navService.goToManage_Integrations();
		}
	}
}
